import { calculateMd5File } from 'helpers/calculate-file-md5';
import { LocalStorageApi } from 'helpers/local-storage';
import {
  nextUploadId,
  publishUploadProgress,
} from 'helpers/upload-progress-bus';
import { UploadFileController } from 'networking/controllers/upload-file.controller';

export type UploadBlobResult = {
  signedId: string;
  blobId: number;
};

/**
 * Uploads a File to S3 using the same presigned-URL flow as useFileUpload,
 * but as a single Promise — useful for batch/headless uploads where
 * progress reporting and React state are not needed.
 */
export const uploadBlobToS3 = async (file: File): Promise<UploadBlobResult> => {
  const md5 = await calculateMd5File(file);
  const presigned = await UploadFileController.presignedUrl(file, md5);

  const uploadId = nextUploadId();
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.directUpload.url);
    xhr.setRequestHeader('X-CSRF-Token', LocalStorageApi.get('csrfToken'));
    Object.entries(presigned.directUpload.headers).forEach(([k, v]) => {
      xhr.setRequestHeader(k, v as string);
    });
    // Feed the live storage indicator (top bar) with real byte progress.
    xhr.upload.addEventListener('progress', (e) => {
      publishUploadProgress({
        uploadId,
        totalBytes: file.size,
        loadedBytes: e.loaded,
      });
    });
    const finish = (ok: boolean) =>
      publishUploadProgress({
        uploadId,
        totalBytes: file.size,
        loadedBytes: ok ? file.size : 0,
        done: true,
      });
    xhr.onload = () => {
      finish(xhr.status >= 200 && xhr.status < 300);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => {
      finish(false);
      reject(new Error('S3 upload network error'));
    };
    xhr.onabort = () => {
      finish(false);
      reject(new Error('S3 upload aborted'));
    };
    xhr.send(file);
  });

  return { signedId: presigned.signedId, blobId: presigned.id };
};
